import { Box, Typography } from "@mui/joy";
import { Check } from "lucide-react";

import type { BillingMode } from "../../interfaces";
import type { IBillingModeOption } from "./constants";

interface IBillingModeCardProps {
    option: IBillingModeOption;
    selected: boolean;
    disabled?: boolean;
    onSelect: (mode: BillingMode) => void;
}

export default function BillingModeCard({ option, selected, disabled, onSelect }: IBillingModeCardProps) {
    const select = () => {
        if (!disabled) onSelect(option.value);
    };

    return (
        <Box
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={select}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    select();
                }
            }}
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                p: 1.5,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                border: "1px solid",
                borderRadius: "4px",
                borderColor: selected ? "primary.solidBg" : "divider",
                bgcolor: selected
                    ? "rgba(var(--standard-palette-primary-mainChannel) / 0.06)"
                    : "background.surface",
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                    borderColor: disabled || selected ? undefined : "neutral.outlinedHoverBorder",
                },
                "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.solidBg",
                    outlineOffset: 2,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    color: selected ? "primary.solidBg" : "neutral.solidBg",
                    backgroundColor: selected
                        ? "rgba(var(--standard-palette-primary-mainChannel) / 0.12)"
                        : "rgba(var(--standard-palette-neutral-mainChannel) / 0.08)",
                }}
            >
                {option.icon}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography level="title-sm">{option.title}</Typography>
                <Typography level="body-xs" color="neutral">
                    {option.description}
                </Typography>
            </Box>

            {selected && (
                <Box sx={{ color: "primary.solidBg", flexShrink: 0, mt: 0.25 }}>
                    <Check size={16} />
                </Box>
            )}
        </Box>
    );
}
